import type { CatalogueEntry } from '@bloo-team/api-client'

/**
 * The Add picker's catalogue rules (docs/sources.md §Connectors · Add): tiles
 * show the type without its trailing "Connector"; the category bar filters on a
 * client-side map of type → category, because the catalogue carries only
 * `{connector_type, logo_img}`. Each type has exactly one category; a type the
 * map does not know lands in Other.
 */

export const CATALOGUE_CATEGORIES = [
  'Cloud Platforms', 'Identity & Access', 'Endpoint & XDR', 'Network & Web',
  'Email & Collab', 'Business SaaS', 'Posture & Intel', 'Generic / Custom', 'Other',
] as const
export type CatalogueCategory = typeof CATALOGUE_CATEGORIES[number]
export type CatalogueCategoryFilter = CatalogueCategory | 'ALL'

/** Keyed by tile name, lower-cased. Cloud is vendor-first: every AWS / Azure / GCP type sits there. */
const CATEGORY_TYPES: Record<Exclude<CatalogueCategory, 'Other'>, string[]> = {
  'Cloud Platforms': [
    'AWS Cloudtrail', 'AWS Cloudwatch', 'AWS GuardDuty', 'AWS Inspector', 'AWS Kinesis', 'AWS S3',
    'AWS S3 Optimized', 'AWS S3 Optimized Cross Account', 'AWS SQS', 'Azure Activity', 'Azure Blob Storage',
    'Azure Eventhub', 'Azure Nsg', 'GCP', 'GCP PubSub', 'Microsoft Sentinel',
  ],
  'Identity & Access': ['1Password', 'Auth0', 'Cisco Duo', 'CyberARK', 'Jumpcloud', 'Okta', 'OneLogin'],
  'Endpoint & XDR': [
    'Cortex XDR', 'CrowdStrike', 'Microsoft Intune', 'SentinelOne', 'Sophos',
    'Trendmicro ApexCentral', 'Trend Micro Audit', 'Trend Micro Vision One',
  ],
  'Network & Web': [
    'Akamai Netstorage', 'Akamai Security Events', 'Cloudflare', 'Cloudflare Logpush', 'Haltdos',
    'Indusface', 'Netflow', 'Netskope', 'NTA',
  ],
  'Email & Collab': [
    'Abnormal Security', 'Box', 'Dropbox', 'Google Workspace', 'Graph API', 'Microsoft ExchangeOnline',
    'Mimecast', 'Office 365', 'Rediffmail Pro', 'Slack', 'Zoom',
  ],
  'Business SaaS': ['Atlassian', 'Github', 'Hubspot', 'Jira', 'Salesforce', 'Salesforce PubSub', 'Shopify', 'Workdayhcm', 'Zendesk'],
  'Posture & Intel': [
    'CloudSEK', 'CybleVision', 'Device42', 'NextDLP Reveal', 'Orca', 'PaloAlto Prisma', 'prisma incidents',
    'Snyk', 'Tenable', 'Tenable Security Center',
  ],
  'Generic / Custom': ['Beats', 'Http', 'Pico', 'RDBMS', 'Snowflake', 'Syslog', 'TCP', 'TLS'],
}

const CATEGORY_OF = new Map<string, CatalogueCategory>(
  Object.entries(CATEGORY_TYPES).flatMap(([cat, names]) => names.map((n) => [n.toLowerCase(), cat as CatalogueCategory])),
)

/** Tile label: the type minus a trailing "Connector" (any case). Titles keep the full type. */
export function tileName(connectorType: string): string {
  return connectorType.replace(/\s+connector\s*$/i, '').trim() || connectorType
}

export function typeCategory(connectorType: string): CatalogueCategory {
  return CATEGORY_OF.get(tileName(connectorType.trim()).toLowerCase()) ?? 'Other'
}

export function categoryCounts(entries: readonly CatalogueEntry[]): Map<CatalogueCategory, number> {
  const m = new Map<CatalogueCategory, number>()
  for (const e of entries) {
    const c = typeCategory(e.connector_type)
    m.set(c, (m.get(c) ?? 0) + 1)
  }
  return m
}

/** Category AND search, sorted by tile name. */
export function filterCatalogue(entries: readonly CatalogueEntry[], f: { category: CatalogueCategoryFilter; query: string }): CatalogueEntry[] {
  const needle = f.query.trim().toLowerCase()
  return entries
    .filter((e) => (f.category === 'ALL' || typeCategory(e.connector_type) === f.category) && (!needle || e.connector_type.toLowerCase().includes(needle)))
    .sort((a, b) => tileName(a.connector_type).localeCompare(tileName(b.connector_type), undefined, { sensitivity: 'base' }))
}
